export const SUPPORTED_NORMALIZED_PROTOCOLS = Object.freeze({
  CAPE_V: 'cape-v',
  CAPE_VR: 'cape-vr',
  OVERALL_SEVERITY: 'all-voiced-overall-severity',
});

const PROTOCOL_BRANCHES = Object.freeze({
  [SUPPORTED_NORMALIZED_PROTOCOLS.CAPE_V]: 'capeV',
  [SUPPORTED_NORMALIZED_PROTOCOLS.CAPE_VR]: 'capeVr',
  [SUPPORTED_NORMALIZED_PROTOCOLS.OVERALL_SEVERITY]: 'overallSeverity',
});

const STANDARD_LABELS = Object.freeze({
  overallSeverity: 'Overall Severity',
  roughness: 'Roughness',
  breathiness: 'Breathiness',
  strain: 'Strain',
  pitch: 'Pitch',
  loudness: 'Loudness',
  resonance: 'Resonance',
  nasality: 'Nasality',
  inconsistencies: 'Inconsistencies',
  instabilities: 'Instabilities',
  additionalFeatures: 'Additional Features',
  overallImpression: 'Overall Impression',
  aphonia: 'Aphonia',
});

const isObject = value => Boolean(value) && typeof value === 'object';
const isNonEmptyString = value => typeof value === 'string' && value.trim() !== '';
const isFiniteNumber = value => typeof value === 'number' && Number.isFinite(value);

const normalizeObservedId = value => {
  const candidate = value?._id?.$oid ?? value?._id ?? value?.$oid ?? value;
  if (candidate == null) return null;
  if (typeof candidate === 'string' || typeof candidate === 'number') {
    const normalized = String(candidate).trim();
    return normalized || null;
  }
  if (typeof candidate?.toHexString === 'function') return candidate.toHexString();
  return null;
};

function legacyAggregationEvidence(assessment) {
  const evaluationToolId = normalizeObservedId(assessment?.evaluationTool);
  const raterSource = isNonEmptyString(assessment?.raterSource)
    ? assessment.raterSource.trim()
    : null;
  const voiceDatabaseId = normalizeObservedId(
    assessment?.voiceDatabaseId ??
    assessment?.voiceSampleId?.identificationData?.voiceDatabaseId
  );
  const missing = [
    !evaluationToolId && 'evaluationTool',
    !raterSource && 'raterSource',
    !voiceDatabaseId && 'voiceDatabaseId',
  ].filter(Boolean);

  if (missing.length) {
    return {
      aggregationEligible: false,
      aggregationIdentity: null,
      legacyCompatibilityKey: null,
      missing,
    };
  }

  const legacyCompatibilityKey = [
    `tool:${evaluationToolId}`,
    `source:${raterSource}`,
    `database:${voiceDatabaseId}`,
  ].join('|');

  return {
    aggregationEligible: true,
    aggregationIdentity: `legacy-observed:${legacyCompatibilityKey}`,
    legacyCompatibilityKey,
    missing: [],
  };
}

export function detectInstrumentResponseFormat(assessment) {
  const hasContract = isObject(assessment?.responseContract);
  const hasResponse = isObject(assessment?.instrumentResponse);

  if (!hasContract && !hasResponse) {
    return { kind: 'legacy', protocolKey: null, representationVersion: null };
  }

  const protocolKey = assessment?.responseContract?.protocolKey || null;
  const representationVersion = assessment?.responseContract?.representationVersion || null;
  const branchKey = PROTOCOL_BRANCHES[protocolKey] || null;

  if (
    !hasContract ||
    !hasResponse ||
    !isNonEmptyString(protocolKey) ||
    !isNonEmptyString(representationVersion) ||
    !branchKey ||
    !isObject(assessment.instrumentResponse[branchKey])
  ) {
    return {
      kind: 'unsupportedNormalized',
      protocolKey,
      representationVersion,
      diagnostic: 'Unsupported or incomplete normalized response envelope.',
    };
  }

  return {
    kind: 'normalized',
    protocolKey,
    protocolVersion: assessment.responseContract.protocolVersion || null,
    representationVersion,
    branchKey,
  };
}

const numericUnit = (key, response, extra = {}) => ({
  key,
  label: STANDARD_LABELS[key] || key,
  kind: 'numeric',
  responseState: response?.status || 'unanswered',
  value: isFiniteNumber(response?.value) ? response.value : null,
  ...extra,
});

const categoryUnit = (key, response) => ({
  key,
  label: STANDARD_LABELS[key] || key,
  kind: 'category',
  responseState: response?.status || 'unanswered',
  category: response?.value || null,
  comment: response?.comment || null,
});

const reviewedListUnit = (key, response) => ({
  key,
  label: STANDARD_LABELS[key] || key,
  kind: 'reviewedList',
  responseState: response?.status || 'unanswered',
  selected: Array.isArray(response?.selected) ? [...response.selected] : [],
  otherLabel: response?.otherLabel || null,
});

const customVasUnit = (response, fallbackKey) => ({
  key: fallbackKey,
  label: response?.label || fallbackKey,
  kind: 'customNumeric',
  responseState: response?.status || 'unused',
  value: isFiniteNumber(response?.value) ? response.value : null,
  consistency: response?.consistency || null,
});

function buildCapeVPresentation(response) {
  const units = ['overallSeverity', 'roughness', 'breathiness', 'strain']
    .map(key => numericUnit(key, response?.[key], {
      consistency: response?.[key]?.consistency || null,
    }));

  ['pitch', 'loudness'].forEach(key => {
    units.push(numericUnit(key, response?.[key], {
      direction: response?.[key]?.direction || null,
      consistency: response?.[key]?.consistency || null,
    }));
  });

  (response?.customVasSlots || []).forEach((slot, index) => {
    units.push(customVasUnit(slot, `customVas${slot?.slot || index + 1}`));
  });

  units.push({
    key: 'resonance',
    label: STANDARD_LABELS.resonance,
    kind: 'category',
    responseState: response?.resonance?.status || 'unanswered',
    category: ['normal', 'other'].includes(response?.resonance?.status) ? response.resonance.status : null,
    comment: response?.resonance?.description || null,
  });
  units.push(reviewedListUnit('additionalFeatures', response?.additionalFeatures));
  units.push({
    key: 'aphonia',
    label: STANDARD_LABELS.aphonia,
    kind: 'notApplicable',
    responseState: response?.aphonia?.status || 'unanswered',
    category: response?.aphonia?.value || null,
    comment: response?.aphonia?.comment || null,
  });

  return units;
}

function buildCapeVrPresentation(response) {
  const units = ['overallSeverity', 'roughness', 'breathiness', 'strain']
    .map(key => numericUnit(key, response?.[key]));

  if (response?.customVas) units.push(customVasUnit(response.customVas, 'customVas'));
  ['pitch', 'loudness', 'resonance', 'nasality'].forEach(key => {
    units.push(categoryUnit(key, response?.[key]));
  });
  units.push({
    key: 'inconsistencies',
    label: STANDARD_LABELS.inconsistencies,
    kind: 'inconsistency',
    responseState: response?.inconsistencies?.status || 'unanswered',
    affectedTasks: (response?.inconsistencies?.affectedTasks || []).map(task => ({ ...task })),
    comment: response?.inconsistencies?.comment || null,
  });
  units.push(reviewedListUnit('instabilities', response?.instabilities));
  units.push(reviewedListUnit('additionalFeatures', response?.additionalFeatures));
  units.push({
    key: 'overallImpression',
    label: STANDARD_LABELS.overallImpression,
    kind: 'text',
    responseState: response?.overallImpression?.status || 'unanswered',
    comment: response?.overallImpression?.text || null,
  });
  return units;
}

// Purpose-bounded read seam for already-authorized CAPE-Vr projections.
// Compatibility remains a backend decision; this helper only preserves the
// accepted CAPE-Vr response-family presentation.
export function buildCapeVrResponseUnits(response) {
  return buildCapeVrPresentation(response);
}

export function formatInstrumentResponseUnit(unit, t = (key, options = {}) => options.defaultValue || key) {
  if (unit.kind === 'numeric' || unit.kind === 'customNumeric') {
    const details = [unit.value == null ? unit.responseState : `${unit.value}/100`];
    if (unit.direction) details.push(t(unit.direction, { defaultValue: unit.direction }));
    if (unit.consistency) details.push(t(unit.consistency, { defaultValue: unit.consistency }));
    return details.join(' · ');
  }
  if (unit.kind === 'category') {
    return [t(unit.category || unit.responseState, { defaultValue: unit.category || unit.responseState }), unit.comment]
      .filter(Boolean).join(' · ');
  }
  if (unit.kind === 'reviewedList') {
    return unit.responseState === 'answered'
      ? unit.selected.length
        ? unit.selected.map(value => t(value, { defaultValue: value })).join(', ')
        : t('feedback_none_selected', { defaultValue: 'Reviewed — none selected' })
      : unit.responseState;
  }
  if (unit.kind === 'inconsistency') {
    if (unit.responseState === 'none') return t('none', { defaultValue: 'None' });
    if (unit.responseState === 'present') {
      return unit.affectedTasks
        .map(task => `${t(task.taskKey, { defaultValue: task.taskKey })}: ${task.description}`)
        .join(' · ');
    }
    return unit.responseState;
  }
  return unit.comment || unit.category || unit.responseState;
}

export function buildInstrumentResponsePresentation(assessment) {
  const detection = detectInstrumentResponseFormat(assessment);
  if (detection.kind !== 'normalized') {
    return { ...detection, units: [], diagnostic: detection.diagnostic || null };
  }

  const response = assessment.instrumentResponse[detection.branchKey];
  let units;
  if (detection.protocolKey === SUPPORTED_NORMALIZED_PROTOCOLS.CAPE_V) {
    units = buildCapeVPresentation(response);
  } else if (detection.protocolKey === SUPPORTED_NORMALIZED_PROTOCOLS.CAPE_VR) {
    units = buildCapeVrPresentation(response);
  } else {
    units = [numericUnit('overallSeverity', response?.overallSeverity)];
  }

  return { ...detection, units };
}

export function buildFeedbackPresentationModel(assessment) {
  const presentation = buildInstrumentResponsePresentation(assessment);
  if (presentation.kind !== 'normalized') {
    return {
      ...presentation,
      evidenceRole: 'unsupported',
      directResponseUnits: [],
    };
  }

  return {
    kind: presentation.kind,
    protocolKey: presentation.protocolKey,
    protocolVersion: presentation.protocolVersion,
    representationVersion: presentation.representationVersion,
    assessmentId: assessment?._id == null ? null : String(assessment._id),
    evidenceRole: 'learner-direct-response',
    directResponseUnits: presentation.units.map(unit => ({ ...unit })),
  };
}

export function buildAuthorizedPvqdContextPresentation(contextResult) {
  const governedIndividual = contextResult?.observationUnit === 'individual-rating-observation' &&
    contextResult?.referenceProvenance?.population?.key ===
      'pvqd.individual-observation.curated.v2' &&
    contextResult?.referenceProvenance?.projection?.key ===
      'pvqd.individual-observation.sample-summary.v1';
  if (
    contextResult?.classification !== 'context-compatible' ||
    contextResult?.evidenceRole !== 'context-compatible' ||
    contextResult?.contextProtocolKey !== 'pvqd-six-quality-vas-100' ||
    contextResult?.sourceRole !== 'pvqdRater' ||
    (contextResult?.observationUnit !== 'ratingObservation' && !governedIndividual) ||
    !Array.isArray(contextResult?.allowedResponseUnits)
  ) {
    return {
      kind: 'unsupportedContext',
      observations: [],
      diagnostic: 'PVQD context requires prior backend authorization.',
    };
  }

  return {
    kind: 'authorizedContext',
    assessmentId: contextResult.assessmentId || null,
    protocolKey: contextResult.contextProtocolKey,
    protocolVersion: contextResult.contextProtocolVersion || null,
    sourceRole: contextResult.sourceRole,
    observationUnit: contextResult.observationUnit,
    ...(contextResult.referenceProvenance
      ? { referenceProvenance: contextResult.referenceProvenance }
      : {}),
    observations: contextResult.allowedResponseUnits.flatMap(unit =>
      isNonEmptyString(unit?.responseUnitKey) && isFiniteNumber(unit?.value)
        ? [{ responseUnitKey: unit.responseUnitKey, value: unit.value }]
        : []
    ),
  };
}

function normalizedNumericUnits(assessment, detection) {
  const response = assessment.instrumentResponse[detection.branchKey];
  const keys = detection.protocolKey === SUPPORTED_NORMALIZED_PROTOCOLS.CAPE_V
    ? ['overallSeverity', 'roughness', 'breathiness', 'strain', 'pitch', 'loudness']
    : detection.protocolKey === SUPPORTED_NORMALIZED_PROTOCOLS.CAPE_VR
      ? ['overallSeverity', 'roughness', 'breathiness', 'strain']
      : ['overallSeverity'];

  const observations = keys.flatMap(key => {
    const unit = response?.[key];
    return unit?.status === 'answered' && isFiniteNumber(unit.value)
      ? [{ responseUnitKey: key, label: STANDARD_LABELS[key] || key, value: unit.value }]
      : [];
  });

  const custom = detection.protocolKey === SUPPORTED_NORMALIZED_PROTOCOLS.CAPE_V
    ? response?.customVasSlots || []
    : detection.protocolKey === SUPPORTED_NORMALIZED_PROTOCOLS.CAPE_VR && response?.customVas
      ? [response.customVas]
      : [];

  custom.forEach((unit, index) => {
    if (unit?.status === 'answered' && isNonEmptyString(unit.label) && isFiniteNumber(unit.value)) {
      observations.push({
        responseUnitKey: `customVas.${unit.slot || index + 1}`,
        label: unit.label.trim(),
        value: unit.value,
      });
    }
  });

  return observations;
}

export function extractDirectNumericObservations(assessment) {
  const detection = detectInstrumentResponseFormat(assessment);
  const assessmentId = assessment?._id == null ? null : String(assessment._id);

  if (detection.kind === 'unsupportedNormalized') {
    return {
      observations: [],
      diagnostics: [{
        code: 'UNSUPPORTED_NORMALIZED_RESPONSE',
        assessmentId,
        protocolKey: detection.protocolKey,
        message: detection.diagnostic,
      }],
    };
  }

  if (detection.kind === 'legacy') {
    const observations = [];
    const aggregationEvidence = legacyAggregationEvidence(assessment);
    const legacyIdentity = {
      aggregationEligible: aggregationEvidence.aggregationEligible,
      aggregationIdentity: aggregationEvidence.aggregationIdentity,
      legacyCompatibilityKey: aggregationEvidence.legacyCompatibilityKey,
    };
    Object.entries(assessment?.perceptualData || {}).forEach(([key, data]) => {
      if (key === 'overallImpression') return;
      if (key === 'customAttributes' && isObject(data)) {
        Object.entries(data).forEach(([customKey, customData]) => {
          if (isFiniteNumber(customData?.VASValue)) {
            observations.push({
              assessmentId,
              protocolKey: 'legacy-unclassified',
              protocolVersion: null,
              responseUnitKey: `customAttributes.${customKey}`,
              label: customData?.attributeName || customKey,
              value: customData.VASValue,
              responseRole: 'directNumeric',
              ...legacyIdentity,
            });
          }
        });
        return;
      }
      if (isFiniteNumber(data?.VASValue)) {
        observations.push({
          assessmentId,
          protocolKey: 'legacy-unclassified',
          protocolVersion: null,
          responseUnitKey: key,
          label: data?.attributeName || key,
          value: data.VASValue,
          responseRole: 'directNumeric',
          ...legacyIdentity,
        });
      }
    });
    return {
      observations,
      diagnostics: observations.length && !aggregationEvidence.aggregationEligible
        ? [{
            code: 'LEGACY_AGGREGATION_IDENTITY_INSUFFICIENT',
            assessmentId,
            protocolKey: 'legacy-unclassified',
            missingIdentityDimensions: aggregationEvidence.missing,
            message: 'Legacy numeric observations remain inspectable but cannot be pooled without complete observed tool/source/database identity.',
          }]
        : [],
    };
  }

  const aggregationIdentity = `protocol:${detection.protocolKey}@${detection.protocolVersion || 'unknown'}`;
  return {
    observations: normalizedNumericUnits(assessment, detection).map(unit => ({
      assessmentId,
      protocolKey: detection.protocolKey,
      protocolVersion: detection.protocolVersion,
      responseUnitKey: unit.responseUnitKey,
      label: unit.label,
      value: unit.value,
      responseRole: 'directNumeric',
      aggregationEligible: true,
      aggregationIdentity,
      legacyCompatibilityKey: null,
    })),
    diagnostics: [],
  };
}

export const canUseFrozenFeedback = assessment =>
  detectInstrumentResponseFormat(assessment).kind === 'legacy';

export function getNormalizedFeedbackGateState(assessment, { provisionalProtocols = [] } = {}) {
  const format = detectInstrumentResponseFormat(assessment);
  if (format.kind === 'legacy') {
    return { path: 'legacy', protocolKey: null, enabled: true };
  }
  if (format.kind === 'normalized') {
    const enabledProtocol = format.protocolKey === SUPPORTED_NORMALIZED_PROTOCOLS.OVERALL_SEVERITY ||
      provisionalProtocols.includes(format.protocolKey);
    return {
      path: enabledProtocol
        ? 'normalized-provisional'
        : 'normalized',
      protocolKey: format.protocolKey,
      enabled: enabledProtocol,
    };
  }
  return {
    path: 'unsupported',
    protocolKey: format.protocolKey || null,
    enabled: false,
  };
}
